import { useState } from 'react'
import toast from 'react-hot-toast'
import {ChevronUp, ChevronDown, Trash2, Plus, Save, X, Edit3, Eye, EyeOff, RefreshCw, Lock, } from 'lucide-react'
import RichTextEditor from '@/components/RichTextEditor'
import {
  useHomeSections,
  HomeSection,
  HomeBackground,
  HomeSectionType,
  SECTION_TYPE_LABELS,
  NATIVE_SECTION_TYPES,
} from '@/hooks/useHomeSections'
import { useAuth } from '@/hooks/useAuth'

const BACKGROUNDS: { value: HomeBackground; label: string; preview: string }[] = [
  { value: 'white', label: 'Blanc', preview: 'bg-white border border-gray-200' },
  { value: 'gray', label: 'Gris clair', preview: 'bg-gray-100 border border-gray-200' },
  { value: 'dark', label: 'Sombre', preview: 'bg-gray-900' },
  { value: 'gradient', label: 'Dégradé rouge → bleu', preview: 'bg-gradient-to-r from-red-500 to-blue-500' },
]

interface DraftSection {
  sectionKey: string
  sectionType: HomeSectionType
  title: string
  subtitle: string
  content: string
  ctaLabel: string
  ctaUrl: string
  ctaLabel2: string
  ctaUrl2: string
  background: HomeBackground
  isActive: boolean
}

const EMPTY_DRAFT: DraftSection = {
  sectionKey: '',
  sectionType: 'custom',
  title: '',
  subtitle: '',
  content: '<p></p>',
  ctaLabel: '',
  ctaUrl: '',
  ctaLabel2: '',
  ctaUrl2: '',
  background: 'white',
  isActive: true,
}

// Quelles sections supportent un 2e CTA ?
const SUPPORTS_CTA2 = (t: HomeSectionType) => t === 'hero' || t === 'cta-final'

export default function HomeEditor() {
  const { user } = useAuth()
  const { items, loading, error, fetchAll, createSection, updateSection, deleteSection, moveSection } = useHomeSections()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<DraftSection>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)

  const sorted = [...items].sort((a, b) => a.order - b.order)

  const beginEdit = (item: HomeSection) => {
    setEditingId(item._id)
    setCreating(false)
    setDraft({
      sectionKey: item.sectionKey,
      sectionType: item.sectionType || 'custom',
      title: item.title,
      subtitle: item.subtitle || '',
      content: item.content,
      ctaLabel: item.ctaLabel || '',
      ctaUrl: item.ctaUrl || '',
      ctaLabel2: item.ctaLabel2 || '',
      ctaUrl2: item.ctaUrl2 || '',
      background: item.background || 'white',
      isActive: item.isActive,
    })
  }

  const beginCreate = () => {
    setCreating(true)
    setEditingId(null)
    setDraft({
      ...EMPTY_DRAFT,
      sectionKey: `section-${Date.now()}`,
      content: '<p></p>',
    })
  }

  const cancel = () => {
    setEditingId(null)
    setCreating(false)
    setDraft(EMPTY_DRAFT)
  }

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast.error('Le titre est requis.')
      return
    }
    if (!draft.sectionKey.trim()) {
      toast.error('La clé de section est requise.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        sectionKey: draft.sectionKey.trim(),
        sectionType: draft.sectionType,
        title: draft.title.trim(),
        subtitle: draft.subtitle.trim() || undefined,
        content: draft.content,
        ctaLabel: draft.ctaLabel.trim() || undefined,
        ctaUrl: draft.ctaUrl.trim() || undefined,
        ctaLabel2: draft.ctaLabel2.trim() || undefined,
        ctaUrl2: draft.ctaUrl2.trim() || undefined,
        background: draft.background,
        isActive: draft.isActive,
      }
      if (creating) {
        const maxOrder = items.reduce((m, i) => Math.max(m, i.order), 0)
        await createSection({ ...payload, order: maxOrder + 1 }, user?.userId)
        toast.success('Section créée.')
      } else if (editingId) {
        await updateSection(editingId, payload)
        toast.success('Section mise à jour.')
      }
      cancel()
    } catch (e) {
      console.error('[HomeEditor] save failed:', e)
      toast.error("Échec de l'enregistrement.")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (item: HomeSection) => {
    const isNative = NATIVE_SECTION_TYPES.includes(item.sectionType)
    if (isNative) {
      if (!window.confirm(
        `Vous êtes sur le point de supprimer la section native « ${SECTION_TYPE_LABELS[item.sectionType]} ».\n\n` +
        `Elle disparaîtra de la page d'accueil. Vous pouvez aussi simplement la masquer.\n\nContinuer ?`
      )) return
    } else {
      if (!window.confirm('Supprimer cette section ? Cette action est définitive.')) return
    }
    try {
      await deleteSection(item._id)
      toast.success('Section supprimée.')
      if (editingId === item._id) cancel()
    } catch (e) {
      console.error('[HomeEditor] delete failed:', e)
      toast.error('Échec de la suppression.')
    }
  }

  const handleMove = async (id: string, direction: 'up' | 'down') => {
    try {
      await moveSection(id, direction)
    } catch (e) {
      console.error('[HomeEditor] move failed:', e)
      toast.error('Échec du déplacement.')
    }
  }

  const handleToggleActive = async (item: HomeSection) => {
    try {
      await updateSection(item._id, { isActive: !item.isActive })
      toast.success(item.isActive ? 'Section masquée.' : 'Section publiée.')
    } catch (e) {
      console.error('[HomeEditor] toggle failed:', e)
      toast.error('Échec de la mise à jour.')
    }
  }

  const isCustomDraft = draft.sectionType === 'custom'
  const draftSupportsCta2 = SUPPORTS_CTA2(draft.sectionType)

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-gray-900">Édition page d'accueil</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Modifiez et réordonnez toutes les sections de la home (hero, univers, mission, offres, formations, témoignages, FAQ, CTA final + sections personnalisées).
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded font-bold">
              {sorted.length} section{sorted.length > 1 ? 's' : ''}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
          <button
            onClick={beginCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-black"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouvelle section
          </button>
        </div>
      </div>

      <div className="p-5">
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{error}</div>
        )}

        <div className="mb-5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900">
          <strong>Astuce :</strong> les sections natives (hero, univers, mission, offres, etc.) gardent leur design figé. Vous pouvez modifier leur titre, sous-titre, contenu et CTA, et les réordonner librement. Les nouvelles sections « personnalisées » utilisent un style libre (titre + contenu HTML + CTA + fond).
        </div>

        {/* Editeur */}
        {(creating || editingId) && (
          <div className="mb-5 border-2 border-gray-900 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">
                {creating ? 'Nouvelle section' : `Modifier — ${SECTION_TYPE_LABELS[draft.sectionType]}`}
              </h3>
              <button onClick={cancel} className="text-gray-400 hover:text-gray-700" title="Annuler">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Type de section : éditable uniquement à la création, et restreint à 'custom' */}
              {creating && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Type de section</label>
                  <div className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg">
                    <Lock className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-700">Section personnalisée (HTML libre)</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Les sections natives (hero, univers, etc.) sont créées automatiquement. Vous ne pouvez créer que des sections personnalisées supplémentaires.
                  </p>
                </div>
              )}

              {!creating && (
                <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg flex items-center gap-2 text-xs">
                  <span className="font-bold text-gray-700">Type :</span>
                  <span className="text-gray-600">{SECTION_TYPE_LABELS[draft.sectionType]}</span>
                  {NATIVE_SECTION_TYPES.includes(draft.sectionType) && (
                    <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-[10px] font-bold uppercase">
                      <Lock className="w-2.5 h-2.5" />
                      Section native
                    </span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Clé technique</label>
                  <input
                    type="text"
                    value={draft.sectionKey}
                    onChange={(e) => setDraft({ ...draft, sectionKey: e.target.value })}
                    disabled={!creating && NATIVE_SECTION_TYPES.includes(draft.sectionType)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 font-mono disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {draft.sectionType === 'hero'
                      ? 'Badge eyebrow (haut du hero)'
                      : draft.sectionType === 'cta-final'
                      ? 'Badge eyebrow (au-dessus du titre)'
                      : 'Sous-titre / eyebrow'}
                  </label>
                  <input
                    type="text"
                    value={draft.subtitle}
                    onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                    placeholder="Ex: Notre mission"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Titre de la section"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                />
                {draft.sectionType === 'hero' && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Astuce : les mots <code className="bg-gray-100 px-1 rounded">BNSSA</code> et <code className="bg-gray-100 px-1 rounded">MNS</code> seront automatiquement colorés.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {draft.sectionType === 'hero'
                    ? 'Phrase d\'accroche (sous le titre)'
                    : 'Contenu (texte enrichi)'}
                </label>
                <RichTextEditor
                  value={draft.content}
                  onChange={(html) => setDraft({ ...draft, content: html })}
                  placeholder="Rédigez le contenu de la section…"
                  minHeight={isCustomDraft ? 220 : 140}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {draftSupportsCta2 ? 'CTA principal — Label' : 'Bouton CTA — Label'}
                  </label>
                  <input
                    type="text"
                    value={draft.ctaLabel}
                    onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })}
                    placeholder="Ex: Voir les offres"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {draftSupportsCta2 ? 'CTA principal — URL' : 'Bouton CTA — URL'}
                  </label>
                  <input
                    type="text"
                    value={draft.ctaUrl}
                    onChange={(e) => setDraft({ ...draft, ctaUrl: e.target.value })}
                    placeholder="/offres"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                  />
                </div>
              </div>

              {draftSupportsCta2 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">CTA secondaire — Label</label>
                    <input
                      type="text"
                      value={draft.ctaLabel2}
                      onChange={(e) => setDraft({ ...draft, ctaLabel2: e.target.value })}
                      placeholder="Ex: Espace recruteur"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">CTA secondaire — URL</label>
                    <input
                      type="text"
                      value={draft.ctaUrl2}
                      onChange={(e) => setDraft({ ...draft, ctaUrl2: e.target.value })}
                      placeholder="/inscription?role=recruiter"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                    />
                  </div>
                </div>
              )}

              {/* Background — uniquement pour custom */}
              {isCustomDraft && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Style de fond</label>
                  <div className="flex flex-wrap gap-2">
                    {BACKGROUNDS.map((bg) => (
                      <button
                        key={bg.value}
                        type="button"
                        onClick={() => setDraft({ ...draft, background: bg.value })}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          draft.background === bg.value
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded ${bg.preview}`} />
                        {bg.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                Section publiée (visible sur la page d'accueil)
              </label>

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button
                  onClick={cancel}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Liste */}
        {loading && sorted.length === 0 && (
          <div className="text-center py-10 text-sm text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Chargement…
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-sm text-gray-500 mb-3">Aucune section pour la page d'accueil.</p>
            <button
              onClick={beginCreate}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-gray-900 rounded-lg hover:bg-black"
            >
              <Plus className="w-3.5 h-3.5" />
              Créer une section
            </button>
          </div>
        )}

        <ul className="space-y-2">
          {sorted.map((item, idx) => {
            const isEditingThis = editingId === item._id
            const isNative = NATIVE_SECTION_TYPES.includes(item.sectionType)
            return (
              <li
                key={item._id}
                className={`border rounded-xl p-4 transition-colors ${
                  isEditingThis
                    ? 'border-gray-900 bg-gray-50'
                    : !item.isActive
                    ? 'border-gray-200 bg-gray-50/50 opacity-60'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        #{idx + 1}
                      </span>
                      {isNative ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
                          <Lock className="w-2.5 h-2.5" />
                          Native
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          Custom
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {item.sectionType}
                      </span>
                      {!item.isActive && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                          Masquée
                        </span>
                      )}
                    </div>
                    {item.subtitle && (
                      <p className="text-[11px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">
                        {item.subtitle}
                      </p>
                    )}
                    <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">{item.title}</h4>
                    <div
                      className="text-xs text-gray-600 leading-relaxed line-clamp-2 [&_p]:inline [&_*]:inline"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                    {(item.ctaLabel || item.ctaLabel2) && (
                      <p className="text-[11px] text-gray-500 mt-1 truncate">
                        {item.ctaLabel && (
                          <>
                            CTA : <strong>{item.ctaLabel}</strong> → <code>{item.ctaUrl || '—'}</code>
                          </>
                        )}
                        {item.ctaLabel2 && (
                          <>
                            {' · '}
                            <strong>{item.ctaLabel2}</strong> → <code>{item.ctaUrl2 || '—'}</code>
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-1">
                      <IconBtn title="Monter" onClick={() => handleMove(item._id, 'up')} disabled={idx === 0}>
                        <ChevronUp className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Descendre"
                        onClick={() => handleMove(item._id, 'down')}
                        disabled={idx === sorted.length - 1}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </IconBtn>
                    </div>
                    <div className="flex items-center gap-1">
                      <IconBtn title={item.isActive ? 'Masquer' : 'Publier'} onClick={() => handleToggleActive(item)}>
                        {item.isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </IconBtn>
                      <IconBtn title="Modifier" onClick={() => beginEdit(item)}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn title="Supprimer" onClick={() => handleDelete(item)} variant="danger">
                        <Trash2 className="w-3.5 h-3.5" />
                      </IconBtn>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function IconBtn({
  children,
  onClick,
  title,
  disabled,
  variant,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  disabled?: boolean
  variant?: 'danger'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        variant === 'danger'
          ? 'border-red-200 text-red-600 bg-white hover:bg-red-50'
          : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  )
}
